import React, { useEffect, useState } from "react";
import { Loader2, Check, Trash2, Mail, Phone, RefreshCw } from "lucide-react";
import { adminListQuotes, adminUpdateQuote, adminDeleteQuote } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

export default function EnquiriesPanel() {
  const { toast } = useToast();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    adminListQuotes()
      .then(setQuotes)
      .catch(() => toast({ title: "Could not load enquiries" }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleHandled = async (q) => {
    try {
      const updated = await adminUpdateQuote(q.id, !q.handled);
      setQuotes((list) => list.map((x) => (x.id === q.id ? updated : x)));
    } catch {
      toast({ title: "Update failed" });
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete enquiry from ${q.name}?`)) return;
    try {
      await adminDeleteQuote(q.id);
      setQuotes((list) => list.filter((x) => x.id !== q.id));
      toast({ title: "Enquiry deleted" });
    } catch {
      toast({ title: "Delete failed" });
    }
  };

  const list = quotes.filter((q) =>
    filter === "all" ? true : filter === "new" ? !q.handled : q.handled
  );
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; } };

  return (
    <div data-testid="enquiries-panel">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex gap-2">
          {["all", "new", "handled"].map((f) => (
            <button key={f} data-testid={`enquiries-filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-heading font-semibold capitalize transition-colors ${filter === f ? "bg-homi-gold text-homi-black" : "bg-white text-homi-black border border-gray-200 hover:bg-homi-gold/10"}`}>
              {f} {f !== "all" && `(${quotes.filter((q) => (f === "new" ? !q.handled : q.handled)).length})`}
            </button>
          ))}
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-sm font-heading font-semibold text-homi-goldDark hover:gap-3 transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-homi-gold" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-20 text-gray-400">No enquiries here yet.</div>
      ) : (
        <div className="grid gap-4">
          {list.map((q) => (
            <div key={q.id} data-testid={`enquiry-card-${q.id}`} className={`bg-white border rounded-2xl p-5 shadow-sm ${q.handled ? "border-green-200" : "border-gray-100"}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading font-bold text-lg text-homi-black">{q.name}</h3>
                    {q.service && <span className="text-xs font-heading font-bold uppercase tracking-wide text-homi-goldDark bg-homi-gold/15 px-2.5 py-1 rounded-full">{q.service}</span>}
                    {q.handled && <span className="text-xs font-heading font-bold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">Handled</span>}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-homi-body flex-wrap">
                    <a href={`mailto:${q.email}`} className="inline-flex items-center gap-1.5 hover:text-homi-goldDark"><Mail className="w-4 h-4" />{q.email}</a>
                    {q.phone && <a href={`tel:${q.phone}`} className="inline-flex items-center gap-1.5 hover:text-homi-goldDark"><Phone className="w-4 h-4" />{q.phone}</a>}
                  </div>
                  {q.message && <p className="mt-3 text-homi-body text-sm bg-gray-50 rounded-lg p-3">{q.message}</p>}
                  <div className="mt-2 text-xs text-gray-400">{fmt(q.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`enquiry-toggle-${q.id}`} onClick={() => toggleHandled(q)} title={q.handled ? "Mark as new" : "Mark as handled"}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${q.handled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500 hover:bg-green-100 hover:text-green-700"}`}>
                    <Check className="w-5 h-5" />
                  </button>
                  <button data-testid={`enquiry-delete-${q.id}`} onClick={() => remove(q)} title="Delete"
                    className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600 transition-colors">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
